import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Copy, Download, X, Check, Moon, Zap, Target, Wrench, ChevronRight } from 'lucide-react';

// ─── Markdown content (mirrors public/downloads/roofclaw-design-standards.md) ───
const DESIGN_STANDARDS_MD = `# RoofClaw Design Standards
> Use this prompt when designing any RoofClaw asset — web, document, slide deck, or table.

---

## Identity

- Dark by default. Background is always near-black (#0D0D0F). Light mode does not exist.
- Purple (#7B61FF) signals intelligence, not decoration. Use it sparingly — max 2 purple elements per viewport.
- Every design decision must reduce cognitive load. Roofing operators are busy.
- Components are built for purpose. Nothing is decorative for its own sake.
- Inter is the only typeface. No serifs. No display fonts. No exceptions.

---

## Colors

| Token | Hex | Usage |
|---|---|---|
| Void | \`#0D0D0F\` | Page backgrounds |
| Elevated | \`#18181B\` | Cards, panels, sidebars |
| Deep | \`#111827\` | Secondary backgrounds |
| Border subtle | \`rgba(240,239,244,0.08)\` | Dividers, card borders |
| Border accent | \`rgba(123,97,255,0.3)\` | Active/focused borders |
| Purple 600 ★ | \`#7B61FF\` | Primary actions, links, eyebrows |
| Purple 700 | \`#6B51EF\` | Hover, pressed states |
| Purple glow | \`rgba(123,97,255,0.2)\` | Box shadows, glows |
| Text primary | \`#F0EFF4\` | Headings, important copy |
| Text secondary | \`#9CA3AF\` | Body, supporting text |
| Text muted | \`#6B7280\` | Captions, timestamps |
| Amber | \`#F59E0B\` | System/SYSTEM blocks, warnings |
| Orange | \`#EA580C\` | Process/THINKS blocks |
| Red | \`#DC2626\` | Errors, RESPONSE blocks, destructive |
| Green | \`#16A34A\` | Success, SKILL blocks |
| Green light | \`#4ADE80\` | On-dark success text/icons |
| Blue | \`#3B82F6\` | Info states |

**Gradients:**
- CTA: \`linear-gradient(135deg, #7B61FF 0%, #6B51EF 100%)\`
- Dark cinematic: \`radial-gradient(ellipse at center, #1a1040 0%, #0D0D0F 70%)\`
- Glow overlay: \`radial-gradient(circle at 50% 0%, rgba(123,97,255,0.15) 0%, transparent 60%)\`

---

## Typography

- Font: Inter (Google Fonts). Always. No exceptions.
- Display/H1: Inter 900, 52px, line-height 1.1
- H2: Inter 800, 36px
- H3: Inter 700, 22px
- H4: Inter 600, 18px
- Body Large: Inter 400, 18px, line-height 1.7
- Body: Inter 400, 16px, line-height 1.6 minimum
- Caption: Inter 400, 14px
- Eyebrow: Inter 700, 11-12px, UPPERCASE, letter-spacing 0.18em, color #7B61FF
- Code inline: monospace, bg #18181B, color #7B61FF

**Rules:**
- Headlines never exceed 52px outside hero contexts
- Eyebrow/badge always precedes a section heading — never standalone
- Tracking-widest only for eyebrows/badges — never for body text
- Never use decorative, serif, or display fonts

---

## Spacing

| Token | Value | Use |
|---|---|---|
| xs | 4px | Micro gaps, icon padding |
| sm | 8px | Tight groupings |
| md | 16px | Default component padding |
| lg | 24px | Component gaps |
| xl | 32px | Section sub-gaps |
| 2xl | 48px | Major section spacing |
| 3xl | 64px | Large section breaks |
| section | 96px | Full section padding |

Container: 1280px max, 1536px ultrawide. Responsive padding: 16-48px. Grid: 12 columns. Card gap: 24-32px.

---

## Components

**Buttons:**
- Primary: bg #7B61FF, text white, px-6 py-3, radius 10px, hover bg #6B51EF
- Secondary: transparent bg, border rgba(123,97,255,0.4), text #F0EFF4
- Ghost: no border, text #7B61FF, hover underline
- Destructive: bg #DC2626, text white

**Badges/Pills:**
- Default: bg #18181B, border rgba(123,97,255,0.3), text #A5B4FC
- Success: bg #052e16, border #16A34A, text #4ADE80
- Warning: bg #451a03, border #F59E0B, text #FCD34D
- Error: bg #450a0a, border #DC2626, text #FCA5A5

**Cards:**
- Standard: bg #18181B, border rgba(240,239,244,0.08), radius 16px, shadow-md
- Featured: add purple-glow shadow + border rgba(123,97,255,0.2)
- Hover: translateY(-2px), shadow card-hover, border rgba(123,97,255,0.3)

**Block Labels:**
- SYSTEM: bg #F59E0B, text #111827
- YOU SAY: bg #E5E7EB, text #111827
- THINKS: bg #EA580C, text white
- RESPONSE: bg #DC2626, text white
- SKILL: bg #16A34A, text white

---

## Tables

### Web Tables
- Header (th): bg #18181B, text #9CA3AF, uppercase, tracking-wider, 12px
- Data (td): text #D1D5DB, 14px; alternating rows transparent / #111827
- Row border: horizontal only (1px rgba(240,239,244,0.06))
- Hover row: bg rgba(123,97,255,0.05)
- Never use visible cell borders

### Document Tables
- Header row: bg #0D0D0F, text white, bold, 11pt
- Data rows: white bg, alternating with #F9F9F9
- Borders: horizontal rules only (1px #E5E7EB) — no vertical lines
- No colored cell backgrounds except status columns

### Slide Deck Tables
- Header: bg #7B61FF, text white, bold
- Rows: bg #18181B alternating #111827, text #F0EFF4
- No visible borders — background contrast only
- Minimum 14pt. Max 6 rows before splitting slides.

---

## Documents

- H1: Inter Bold 24pt, #111827 (print) or #F0EFF4 (screen), caps + purple underline bar
- H2: Inter SemiBold 16pt / H3: Inter SemiBold 13pt / Body: Inter Regular 11pt
- Header: wordmark left, title center, client+date right, 2px #7B61FF rule below
- Body text: #111827 for print (not pure black)
- Callout boxes: 4px border-left + 5% bg tint. Info/Warning/Tip/Critical.
- Margins: 1 inch all sides. Footer: page # right + "RoofClaw · roofclaw.com" left.

---

## Slides

- Background: always #0D0D0F. Module breaks: radial-gradient dark purple.
- Safe zone: 80px padding. Heading: top 20%. Content: middle 60%. Caption: bottom 20%.
- Display: Inter 900 52-64px / Section: Inter 700 36px / Body: Inter 600 22px
- Progressive reveal: one concept per click. Max 3-4 per slide. Captions last.
- Use SYSTEM/THINKS/RESPONSE/SKILL/YOU SAY block system throughout.

---

## Never Do

- Never use pure black #000000 — use #0D0D0F
- Never use pure white #FFFFFF — use #F0EFF4
- Never use more than 2 purple elements per viewport
- Never use shadows from pure black — always from #0A0A14
- Never use non-Inter typefaces
- Never use pill-style buttons — use 10-12px radius
- Never use visible cell borders (vertical gridlines) in tables
- Never create light-mode layouts
- Never use tracking-widest on body text
- Never place an eyebrow without a heading following it
- Never stretch or distort the RoofClaw wordmark
- Never show more than 3-4 concepts on a single slide
- Never use Calendly links — use HubSpot meeting links only
`;

// ─── Section nav config ───
const SECTIONS = [
  { id: 'personality',  label: '01 Brand Personality' },
  { id: 'logo',         label: '02 Logo & Wordmark' },
  { id: 'colors',       label: '03 Color Palette' },
  { id: 'typography',   label: '04 Typography' },
  { id: 'spacing',      label: '05 Spacing & Layout' },
  { id: 'components',   label: '06 Components' },
  { id: 'tables',       label: '07 Tables' },
  { id: 'documents',    label: '08 Documents' },
  { id: 'slides',       label: '09 Slide Decks' },
  { id: 'glass',        label: '10 Glass & Effects' },
  { id: 'animation',    label: '11 Animation' },
  { id: 'prompt',       label: '12 AI Prompt' },
];

// ─── Reusable components ───

function SectionHeader({ num, title }) {
  return (
    <div className="mb-10">
      <p style={{ fontFamily: 'Inter, sans-serif', fontWeight: 700, fontSize: 11, letterSpacing: '0.18em', color: '#7B61FF', textTransform: 'uppercase', marginBottom: 12 }}>
        {num}
      </p>
      <h2 style={{ fontFamily: 'Inter, sans-serif', fontWeight: 800, fontSize: 36, color: '#F0EFF4', lineHeight: 1.15 }}>
        {title}
      </h2>
    </div>
  );
}

function CodeBlock({ children }) {
  const [copied, setCopied] = useState(false);
  const handleCopy = () => {
    navigator.clipboard.writeText(children);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };
  return (
    <div style={{ position: 'relative', background: '#18181B', border: '1px solid rgba(240,239,244,0.08)', borderRadius: 12, overflow: 'hidden' }}>
      <button
        onClick={handleCopy}
        style={{ position: 'absolute', top: 10, right: 10, background: 'rgba(123,97,255,0.15)', border: '1px solid rgba(123,97,255,0.3)', borderRadius: 6, padding: '4px 8px', color: '#A5B4FC', cursor: 'pointer', fontSize: 12, display: 'flex', alignItems: 'center', gap: 4 }}
      >
        {copied ? <Check size={12} /> : <Copy size={12} />}
        {copied ? 'Copied' : 'Copy'}
      </button>
      <pre style={{ margin: 0, padding: '20px 16px', fontFamily: 'monospace', fontSize: 13, color: '#7B61FF', lineHeight: 1.6, overflowX: 'auto', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
        {children}
      </pre>
    </div>
  );
}

function InlineCode({ children }) {
  return (
    <code style={{ fontFamily: 'monospace', background: '#18181B', color: '#7B61FF', padding: '2px 6px', borderRadius: 4, fontSize: '0.9em' }}>
      {children}
    </code>
  );
}

function RuleCallout({ items, label = "Rules" }) {
  return (
    <div style={{ background: 'rgba(123,97,255,0.08)', border: '1px solid rgba(123,97,255,0.2)', borderRadius: 12, padding: '20px 24px', marginTop: 24 }}>
      <p style={{ fontFamily: 'Inter, sans-serif', fontWeight: 700, fontSize: 11, letterSpacing: '0.18em', color: '#7B61FF', textTransform: 'uppercase', marginBottom: 12 }}>{label}</p>
      <ul style={{ margin: 0, padding: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 8 }}>
        {items.map((item, i) => (
          <li key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 10, color: '#D1D5DB', fontSize: 14, lineHeight: 1.6 }}>
            <span style={{ color: '#7B61FF', marginTop: 2, flexShrink: 0 }}>→</span>
            <span>{item}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

// ─── AI Prompt Modal ───
function PromptModal({ open, onClose }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(DESIGN_STANDARDS_MD);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  useEffect(() => {
    const handleKey = (e) => { if (e.key === 'Escape') onClose(); };
    if (open) document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 1000,
        background: 'rgba(0,0,0,0.75)',
        backdropFilter: 'blur(8px)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: '24px',
        animation: 'fadeIn 200ms cubic-bezier(0.16,1,0.3,1)',
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          background: '#18181B',
          border: '1px solid rgba(123,97,255,0.3)',
          borderRadius: 20,
          width: '100%',
          maxWidth: 720,
          maxHeight: '85vh',
          display: 'flex',
          flexDirection: 'column',
          boxShadow: '0 0 40px rgba(123,97,255,0.2), 0 20px 60px rgba(10,10,20,0.6)',
          animation: 'slideUp 300ms cubic-bezier(0.16,1,0.3,1)',
        }}
      >
        {/* Modal header */}
        <div style={{ padding: '24px 28px 20px', borderBottom: '1px solid rgba(240,239,244,0.08)', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexShrink: 0 }}>
          <div>
            <p style={{ fontFamily: 'Inter, sans-serif', fontWeight: 700, fontSize: 11, letterSpacing: '0.18em', color: '#7B61FF', textTransform: 'uppercase', marginBottom: 6 }}>
              AI DESIGN PROMPT
            </p>
            <h3 style={{ fontFamily: 'Inter, sans-serif', fontWeight: 800, fontSize: 22, color: '#F0EFF4', margin: 0, lineHeight: 1.2 }}>
              RoofClaw Design Standards
            </h3>
            <p style={{ color: '#9CA3AF', fontSize: 14, marginTop: 6, lineHeight: 1.5 }}>
              Paste into any AI tool before asking it to design RoofClaw assets.
            </p>
          </div>
          <button
            onClick={onClose}
            style={{ background: 'rgba(240,239,244,0.08)', border: 'none', borderRadius: 8, width: 36, height: 36, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: '#9CA3AF', flexShrink: 0, marginLeft: 16 }}
          >
            <X size={18} />
          </button>
        </div>

        {/* Modal content */}
        <div style={{ flex: 1, overflow: 'auto', padding: '20px 28px' }}>
          <pre style={{
            margin: 0,
            fontFamily: 'monospace',
            fontSize: 13,
            color: '#D1D5DB',
            lineHeight: 1.7,
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
            background: '#0D0D0F',
            border: '1px solid rgba(240,239,244,0.06)',
            borderRadius: 10,
            padding: 20,
          }}>
            {DESIGN_STANDARDS_MD}
          </pre>
        </div>

        {/* Modal footer */}
        <div style={{ padding: '16px 28px 24px', borderTop: '1px solid rgba(240,239,244,0.08)', display: 'flex', gap: 12, flexShrink: 0 }}>
          <button
            onClick={handleCopy}
            style={{
              flex: 1,
              background: copied ? 'rgba(22,163,74,0.2)' : 'linear-gradient(135deg, #7B61FF 0%, #6B51EF 100%)',
              border: copied ? '1px solid #16A34A' : 'none',
              borderRadius: 10,
              padding: '12px 20px',
              color: copied ? '#4ADE80' : 'white',
              fontFamily: 'Inter, sans-serif',
              fontWeight: 600,
              fontSize: 14,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 8,
              transition: 'all 200ms',
            }}
          >
            {copied ? <Check size={16} /> : <Copy size={16} />}
            {copied ? 'Copied to Clipboard!' : 'Copy to Clipboard'}
          </button>
          <a
            href="/downloads/roofclaw-design-standards.md"
            download="roofclaw-design-standards.md"
            style={{
              background: 'rgba(240,239,244,0.06)',
              border: '1px solid rgba(240,239,244,0.12)',
              borderRadius: 10,
              padding: '12px 20px',
              color: '#F0EFF4',
              fontFamily: 'Inter, sans-serif',
              fontWeight: 600,
              fontSize: 14,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              textDecoration: 'none',
              transition: 'background 200ms',
            }}
          >
            <Download size={16} />
            Download .md
          </a>
        </div>
      </div>
    </div>
  );
}

// ─── Main Component ───
export default function BrandKit() {
  const [modalOpen, setModalOpen] = useState(false);
  const [activeSection, setActiveSection] = useState('personality');
  const sectionRefs = useRef({});

  useEffect(() => { window.scrollTo(0, 0); }, []);

  // Intersection observer for active nav dot
  useEffect(() => {
    const observers = [];
    SECTIONS.forEach(({ id }) => {
      const el = document.getElementById(`section-${id}`);
      if (!el) return;
      const obs = new IntersectionObserver(
        ([entry]) => { if (entry.isIntersecting) setActiveSection(id); },
        { rootMargin: '-30% 0px -60% 0px', threshold: 0 }
      );
      obs.observe(el);
      observers.push(obs);
    });
    return () => observers.forEach(o => o.disconnect());
  }, []);

  const scrollTo = (id) => {
    const el = document.getElementById(`section-${id}`);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const s = {
    page: {
      minHeight: '100vh',
      background: '#0D0D0F',
      color: '#F0EFF4',
      fontFamily: 'Inter, sans-serif',
    },
    container: {
      maxWidth: 1280,
      margin: '0 auto',
      padding: '0 24px',
    },
    section: {
      paddingTop: 96,
      paddingBottom: 64,
      borderBottom: '1px solid rgba(240,239,244,0.06)',
    },
  };

  return (
    <div style={s.page}>
      <style>{`
        @keyframes fadeIn { from { opacity: 0 } to { opacity: 1 } }
        @keyframes slideUp { from { opacity: 0; transform: translateY(20px) } to { opacity: 1; transform: translateY(0) } }
        @media (prefers-reduced-motion: reduce) { *, *::before, *::after { animation-duration: 0ms !important; transition-duration: 0ms !important; } }
      `}</style>

      <PromptModal open={modalOpen} onClose={() => setModalOpen(false)} />

      {/* Sticky sidebar nav (desktop only) */}
      <div style={{
        position: 'fixed', left: 24, top: '50%', transform: 'translateY(-50%)',
        zIndex: 50, display: 'flex', flexDirection: 'column', gap: 6,
      }}
        className="brand-kit-nav"
      >
        <style>{`.brand-kit-nav { display: none; } @media (min-width: 1400px) { .brand-kit-nav { display: flex; } }`}</style>
        {SECTIONS.map(({ id, label }) => (
          <button
            key={id}
            onClick={() => scrollTo(id)}
            title={label}
            style={{
              width: 8, height: 8, borderRadius: '50%', border: 'none', cursor: 'pointer',
              background: activeSection === id ? '#7B61FF' : 'rgba(240,239,244,0.2)',
              transition: 'all 200ms',
              padding: 0,
            }}
          />
        ))}
      </div>

      <div style={s.container}>

        {/* ── Hero Header ── */}
        <div style={{ paddingTop: 80, paddingBottom: 64, textAlign: 'center', position: 'relative' }}>
          {/* Glow overlay */}
          <div style={{ position: 'absolute', inset: 0, background: 'radial-gradient(circle at 50% 0%, rgba(123,97,255,0.12) 0%, transparent 55%)', pointerEvents: 'none' }} />

          <p style={{ fontWeight: 700, fontSize: 11, letterSpacing: '0.18em', color: '#7B61FF', textTransform: 'uppercase', marginBottom: 20 }}>
            ROOFCLAW DESIGN SYSTEM
          </p>
          <h1 style={{ fontWeight: 900, fontSize: 'clamp(40px,6vw,64px)', lineHeight: 1.05, color: '#F0EFF4', marginBottom: 20 }}>
            The Design Language
          </h1>
          <p style={{ fontSize: 18, color: '#9CA3AF', lineHeight: 1.7, maxWidth: 580, margin: '0 auto 40px' }}>
            Standards for web, documents, tables, and slide decks —<br />for humans and AI agents building RoofClaw assets.
          </p>
          <div style={{ display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap' }}>
            <button
              onClick={() => setModalOpen(true)}
              style={{
                background: 'linear-gradient(135deg, #7B61FF 0%, #6B51EF 100%)',
                border: 'none', borderRadius: 10, padding: '12px 24px',
                color: 'white', fontWeight: 600, fontSize: 15, cursor: 'pointer',
                display: 'flex', alignItems: 'center', gap: 8,
                boxShadow: '0 0 20px rgba(123,97,255,0.3)',
              }}
            >
              <Copy size={16} />
              Copy AI Design Prompt
            </button>
            <a
              href="/downloads/roofclaw-design-standards.md"
              download="roofclaw-design-standards.md"
              style={{
                background: 'rgba(240,239,244,0.06)',
                border: '1px solid rgba(240,239,244,0.12)',
                borderRadius: 10, padding: '12px 24px',
                color: '#F0EFF4', fontWeight: 600, fontSize: 15,
                display: 'flex', alignItems: 'center', gap: 8,
                textDecoration: 'none',
              }}
            >
              <Download size={16} />
              Download .md
            </a>
          </div>

          {/* Section quick-nav */}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, justifyContent: 'center', marginTop: 40 }}>
            {SECTIONS.map(({ id, label }) => (
              <button
                key={id}
                onClick={() => scrollTo(id)}
                style={{
                  background: 'rgba(240,239,244,0.04)',
                  border: '1px solid rgba(240,239,244,0.08)',
                  borderRadius: 6, padding: '5px 12px',
                  color: '#9CA3AF', fontSize: 12, cursor: 'pointer',
                  transition: 'all 200ms',
                  fontFamily: 'Inter, sans-serif',
                }}
                onMouseEnter={e => { e.currentTarget.style.background = 'rgba(123,97,255,0.1)'; e.currentTarget.style.borderColor = 'rgba(123,97,255,0.3)'; e.currentTarget.style.color = '#A5B4FC'; }}
                onMouseLeave={e => { e.currentTarget.style.background = 'rgba(240,239,244,0.04)'; e.currentTarget.style.borderColor = 'rgba(240,239,244,0.08)'; e.currentTarget.style.color = '#9CA3AF'; }}
              >
                {label}
              </button>
            ))}
          </div>
        </div>

        {/* ═══════════════════════════════════════════
            01 — Brand Personality
        ═══════════════════════════════════════════ */}
        <div id="section-personality" style={s.section}>
          <SectionHeader num="01" title="Brand Personality" />
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 24 }}>
            {[
              { icon: <Moon size={24} />, title: 'Dark by Default', desc: "Near-black backgrounds. We don't do white-on-white. Light mode is not a consideration." },
              { icon: <Zap size={24} />, title: 'Expert, Not Flashy', desc: 'Purple accents signal intelligence, not hype. We use them with intention, not decoration.' },
              { icon: <Target size={24} />, title: 'Direct & Specific', desc: 'Roofing operators are busy. Every design decision should reduce cognitive load, not perform.' },
              { icon: <Wrench size={24} />, title: 'Built, Not Decorated', desc: "Components look like they were made for a purpose. Nothing is there for aesthetics alone." },
            ].map(({ icon, title, desc }) => (
              <div key={title} style={{
                background: '#18181B', border: '1px solid rgba(240,239,244,0.08)',
                borderRadius: 16, padding: 28,
                transition: 'border-color 300ms, box-shadow 300ms',
              }}
                onMouseEnter={e => { e.currentTarget.style.borderColor = 'rgba(123,97,255,0.3)'; e.currentTarget.style.boxShadow = '0 8px 32px rgba(123,97,255,0.1)'; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = 'rgba(240,239,244,0.08)'; e.currentTarget.style.boxShadow = 'none'; }}
              >
                <div style={{ color: '#7B61FF', marginBottom: 16 }}>{icon}</div>
                <h3 style={{ fontWeight: 700, fontSize: 18, color: '#F0EFF4', marginBottom: 10 }}>{title}</h3>
                <p style={{ color: '#9CA3AF', fontSize: 15, lineHeight: 1.6, margin: 0 }}>{desc}</p>
              </div>
            ))}
          </div>
        </div>

        {/* ═══════════════════════════════════════════
            02 — Logo & Wordmark
        ═══════════════════════════════════════════ */}
        <div id="section-logo" style={s.section}>
          <SectionHeader num="02" title="Logo & Wordmark" />

          <div style={{ background: '#18181B', border: '1px solid rgba(240,239,244,0.08)', borderRadius: 12, padding: '12px 20px', marginBottom: 32, display: 'flex', alignItems: 'flex-start', gap: 12 }}>
            <span style={{ fontSize: 20, flexShrink: 0 }}>ℹ️</span>
            <p style={{ color: '#D1D5DB', fontSize: 15, lineHeight: 1.6, margin: 0 }}>
              RoofClaw does not currently have a graphic logo mark. The brand uses a text wordmark: <strong style={{ color: '#F0EFF4' }}>RoofClaw</strong> in Inter 800. When a logomark is created, it will appear here.
            </p>
          </div>

          {/* Wordmark specimens */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 20, marginBottom: 32 }}>
            <div style={{ background: '#0D0D0F', border: '1px solid rgba(240,239,244,0.08)', borderRadius: 16, padding: '48px 32px', textAlign: 'center' }}>
              <p style={{ fontFamily: 'Inter, sans-serif', fontWeight: 800, fontSize: 40, color: '#F0EFF4', margin: 0 }}>RoofClaw</p>
              <p style={{ color: '#6B7280', fontSize: 12, marginTop: 16, fontFamily: 'monospace' }}>White on Void — Primary</p>
            </div>
            <div style={{ background: '#18181B', border: '1px solid rgba(240,239,244,0.08)', borderRadius: 16, padding: '48px 32px', textAlign: 'center' }}>
              <p style={{ fontFamily: 'Inter, sans-serif', fontWeight: 800, fontSize: 40, color: '#7B61FF', margin: 0 }}>RoofClaw</p>
              <p style={{ color: '#6B7280', fontSize: 12, marginTop: 16, fontFamily: 'monospace' }}>Purple on Elevated — Accent</p>
            </div>
            <div style={{ background: '#F0EFF4', border: '1px solid rgba(240,239,244,0.08)', borderRadius: 16, padding: '48px 32px', textAlign: 'center' }}>
              <p style={{ fontFamily: 'Inter, sans-serif', fontWeight: 800, fontSize: 40, color: '#0D0D0F', margin: 0 }}>RoofClaw</p>
              <p style={{ color: '#6B7280', fontSize: 12, marginTop: 16, fontFamily: 'monospace' }}>Void on Light — Rare</p>
            </div>
          </div>

          <RuleCallout label="Wordmark Rules" items={[
            "Always Inter 800. Never a different weight or style.",
            "Never stretch, skew, or distort the wordmark.",
            "Never place on a colored background that reduces contrast.",
            "Never use decorative or script treatments.",
            "Acceptable backgrounds: #0D0D0F, #18181B, #111827, or #F0EFF4 (rare).",
          ]} />
        </div>

        {/* ═══════════════════════════════════════════
            03 — Color Palette
        ═══════════════════════════════════════════ */}
        <div id="section-colors" style={s.section}>
          <SectionHeader num="03" title="Color Palette" />

          {[
            {
              label: 'Primary Backgrounds', colors: [
                { name: 'Void', hex: '#0D0D0F', bg: '#0D0D0F', text: '#F0EFF4', usage: 'Page backgrounds', border: true },
                { name: 'Elevated', hex: '#18181B', bg: '#18181B', text: '#F0EFF4', usage: 'Cards, panels, sidebars' },
                { name: 'Deep', hex: '#111827', bg: '#111827', text: '#F0EFF4', usage: 'Secondary backgrounds' },
                { name: 'Border', hex: 'rgba(240,239,244,0.08)', bg: 'rgba(240,239,244,0.08)', text: '#9CA3AF', usage: 'Subtle dividers', small: true },
              ]
            },
            {
              label: 'Accent — Purple', colors: [
                { name: 'Purple 600 ★', hex: '#7B61FF', bg: '#7B61FF', text: 'white', usage: 'Primary actions, links, eyebrows', star: true },
                { name: 'Purple 700', hex: '#6B51EF', bg: '#6B51EF', text: 'white', usage: 'Hover states, pressed buttons' },
                { name: 'Purple Glow', hex: 'rgba(123,97,255,0.2)', bg: 'rgba(123,97,255,0.2)', text: '#A5B4FC', usage: 'Box shadows, glow effects', small: true },
              ]
            },
            {
              label: 'Semantic', colors: [
                { name: 'Amber', hex: '#F59E0B', bg: '#F59E0B', text: '#111', usage: 'SYSTEM blocks, warnings' },
                { name: 'Orange', hex: '#EA580C', bg: '#EA580C', text: 'white', usage: 'THINKS / process states' },
                { name: 'Red', hex: '#DC2626', bg: '#DC2626', text: 'white', usage: 'Errors, RESPONSE blocks' },
                { name: 'Green', hex: '#16A34A', bg: '#16A34A', text: 'white', usage: 'Success, SKILL blocks' },
                { name: 'Green Light', hex: '#4ADE80', bg: '#4ADE80', text: '#052e16', usage: 'On-dark success text/icons' },
                { name: 'Blue', hex: '#3B82F6', bg: '#3B82F6', text: 'white', usage: 'Info states' },
              ]
            },
            {
              label: 'Text', colors: [
                { name: 'Primary', hex: '#F0EFF4', bg: '#F0EFF4', text: '#0D0D0F', usage: 'Headings, important copy' },
                { name: 'Secondary', hex: '#9CA3AF', bg: '#9CA3AF', text: '#0D0D0F', usage: 'Body, supporting text' },
                { name: 'Muted', hex: '#6B7280', bg: '#6B7280', text: '#F0EFF4', usage: 'Captions, metadata' },
              ]
            },
          ].map(({ label, colors }) => (
            <div key={label} style={{ marginBottom: 40 }}>
              <p style={{ fontFamily: 'Inter, sans-serif', fontWeight: 600, fontSize: 13, color: '#6B7280', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 16 }}>{label}</p>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 16 }}>
                {colors.map(({ name, hex, bg, text, usage, border, star }) => (
                  <div key={name} style={{ background: '#18181B', border: '1px solid rgba(240,239,244,0.08)', borderRadius: 12, overflow: 'hidden' }}>
                    <div style={{
                      height: 80, background: bg,
                      border: border ? '1px solid rgba(240,239,244,0.15)' : 'none',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}>
                      {star && <span style={{ fontSize: 10, color: 'rgba(255,255,255,0.6)', fontFamily: 'monospace' }}>★ PRIMARY</span>}
                    </div>
                    <div style={{ padding: '12px 16px' }}>
                      <p style={{ fontWeight: 600, fontSize: 14, color: '#F0EFF4', margin: '0 0 4px' }}>{name}</p>
                      <p style={{ fontFamily: 'monospace', fontSize: 12, color: '#7B61FF', margin: '0 0 6px' }}>{hex}</p>
                      <p style={{ fontSize: 12, color: '#6B7280', margin: 0, lineHeight: 1.4 }}>{usage}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}

          {/* Gradients */}
          <p style={{ fontFamily: 'Inter, sans-serif', fontWeight: 600, fontSize: 13, color: '#6B7280', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 16 }}>Gradients</p>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 16, marginBottom: 32 }}>
            {[
              { name: 'CTA', gradient: 'linear-gradient(135deg, #7B61FF 0%, #6B51EF 100%)', code: 'linear-gradient(135deg, #7B61FF 0%, #6B51EF 100%)' },
              { name: 'Dark Cinematic', gradient: 'radial-gradient(ellipse at center, #1a1040 0%, #0D0D0F 70%)', code: 'radial-gradient(ellipse at center, #1a1040 0%, #0D0D0F 70%)' },
              { name: 'Glow Overlay', gradient: 'radial-gradient(circle at 50% 0%, rgba(123,97,255,0.3) 0%, transparent 60%)', code: 'radial-gradient(circle at 50% 0%, rgba(123,97,255,0.15) 0%, transparent 60%)' },
            ].map(({ name, gradient, code }) => (
              <div key={name} style={{ background: '#18181B', border: '1px solid rgba(240,239,244,0.08)', borderRadius: 12, overflow: 'hidden' }}>
                <div style={{ height: 100, background: gradient }} />
                <div style={{ padding: '14px 16px' }}>
                  <p style={{ fontWeight: 600, fontSize: 14, color: '#F0EFF4', margin: '0 0 6px' }}>{name}</p>
                  <InlineCode>{code}</InlineCode>
                </div>
              </div>
            ))}
          </div>

          <RuleCallout label="Critical Color Rules" items={[
            "Never use pure black #000000 — always use #0D0D0F",
            "Never use pure white #FFFFFF — always use #F0EFF4",
            "Purple is for signal, not decoration — max 2 purple elements per viewport",
            "All shadows derive from #0A0A14, never pure black",
          ]} />
        </div>

        {/* ═══════════════════════════════════════════
            04 — Typography
        ═══════════════════════════════════════════ */}
        <div id="section-typography" style={s.section}>
          <SectionHeader num="04" title="Typography" />

          <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
            {[
              { label: 'Display / H1', spec: 'Inter 900 · 52px · line-height 1.1', size: 52, weight: 900, text: 'RoofClaw turns electricity into revenue.' },
              { label: 'H2', spec: 'Inter 800 · 36px', size: 36, weight: 800, text: 'Your agent works while you sleep.' },
              { label: 'H3', spec: 'Inter 700 · 22px', size: 22, weight: 700, text: 'Connected to ProLine. Synced nightly.' },
              { label: 'H4', spec: 'Inter 600 · 18px', size: 18, weight: 600, text: 'Three new leads since 7am.' },
              { label: 'Body Large', spec: 'Inter 400 · 18px · line-height 1.7', size: 18, weight: 400, text: 'RoofClaw is a fully configured AI assistant running on your dedicated Apple hardware. It connects to your CRM, your calendar, your email — and works 24/7.' },
              { label: 'Body', spec: 'Inter 400 · 16px · line-height 1.6', size: 16, weight: 400, text: 'Pre-configured with up to 3 API integrations. Delivered to your door. Ready day one.' },
            ].map(({ label, spec, size, weight, text }) => (
              <div key={label} style={{ background: '#18181B', border: '1px solid rgba(240,239,244,0.08)', borderRadius: 16, padding: '24px 28px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, flexWrap: 'wrap', gap: 8 }}>
                  <span style={{ fontFamily: 'monospace', fontSize: 12, color: '#7B61FF' }}>{label}</span>
                  <span style={{ fontSize: 12, color: '#6B7280' }}>{spec}</span>
                </div>
                <p style={{ fontFamily: 'Inter, sans-serif', fontWeight: weight, fontSize: Math.min(size, 48), lineHeight: weight === 900 ? 1.1 : 1.6, color: '#F0EFF4', margin: 0 }}>
                  {text}
                </p>
              </div>
            ))}

            {/* Eyebrow specimen */}
            <div style={{ background: '#18181B', border: '1px solid rgba(240,239,244,0.08)', borderRadius: 16, padding: '24px 28px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, flexWrap: 'wrap', gap: 8 }}>
                <span style={{ fontFamily: 'monospace', fontSize: 12, color: '#7B61FF' }}>Eyebrow / Badge</span>
                <span style={{ fontSize: 12, color: '#6B7280' }}>Inter 700 · 11-12px · uppercase · tracking 0.18em</span>
              </div>
              <p style={{ fontFamily: 'Inter, sans-serif', fontWeight: 700, fontSize: 12, letterSpacing: '0.18em', color: '#7B61FF', textTransform: 'uppercase', margin: 0 }}>
                ROOFCLAW · SKILL RELEASE · V2.0
              </p>
            </div>

            {/* Code inline */}
            <div style={{ background: '#18181B', border: '1px solid rgba(240,239,244,0.08)', borderRadius: 16, padding: '24px 28px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, flexWrap: 'wrap', gap: 8 }}>
                <span style={{ fontFamily: 'monospace', fontSize: 12, color: '#7B61FF' }}>Code Inline</span>
                <span style={{ fontSize: 12, color: '#6B7280' }}>monospace · bg #18181B · color #7B61FF</span>
              </div>
              <p style={{ color: '#D1D5DB', fontSize: 16, margin: 0 }}>
                The agent runs <InlineCode>daily-sync.py</InlineCode> every night at midnight — pulling from <InlineCode>proline-api.js</InlineCode> into your local CRM.
              </p>
            </div>
          </div>

          <RuleCallout label="Typography Rules" items={[
            "Headlines never exceed 52px outside hero contexts",
            "Body line-height minimum 1.6 — always",
            "Eyebrow/badge always precedes a section heading — never standalone",
            "Never use decorative or serif fonts",
            "Tracking-widest only for eyebrows/badges — never for body text",
          ]} />
        </div>

        {/* ═══════════════════════════════════════════
            05 — Spacing & Layout
        ═══════════════════════════════════════════ */}
        <div id="section-spacing" style={s.section}>
          <SectionHeader num="05" title="Spacing & Layout" />

          <div style={{ background: '#18181B', border: '1px solid rgba(240,239,244,0.08)', borderRadius: 16, overflow: 'hidden', marginBottom: 32 }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: '#18181B', borderBottom: '1px solid rgba(240,239,244,0.08)' }}>
                  {['Token', 'Value', 'Visual', 'Use'].map(h => (
                    <th key={h} style={{ padding: '12px 20px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: '0.1em' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {[
                  { token: 'xs', value: '4px', use: 'Micro gaps, icon padding' },
                  { token: 'sm', value: '8px', use: 'Tight groupings' },
                  { token: 'md', value: '16px', use: 'Default component padding' },
                  { token: 'lg', value: '24px', use: 'Component gaps' },
                  { token: 'xl', value: '32px', use: 'Section sub-gaps' },
                  { token: '2xl', value: '48px', use: 'Major section spacing' },
                  { token: '3xl', value: '64px', use: 'Large section breaks' },
                  { token: 'section', value: '96px', use: 'Full section padding' },
                ].map(({ token, value, use }, i) => {
                  const px = parseInt(value);
                  return (
                    <tr key={token} style={{ borderBottom: '1px solid rgba(240,239,244,0.06)', background: i % 2 === 1 ? '#111827' : 'transparent' }}>
                      <td style={{ padding: '12px 20px', fontFamily: 'monospace', fontSize: 13, color: '#7B61FF' }}>{token}</td>
                      <td style={{ padding: '12px 20px', fontFamily: 'monospace', fontSize: 13, color: '#D1D5DB' }}>{value}</td>
                      <td style={{ padding: '12px 20px' }}>
                        <div style={{ height: 8, width: Math.min(px * 2, 200), background: '#7B61FF', borderRadius: 4, opacity: 0.6 }} />
                      </td>
                      <td style={{ padding: '12px 20px', fontSize: 13, color: '#9CA3AF' }}>{use}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 16 }}>
            {[
              { label: 'Max Container', value: '1280px (1536px ultrawide)' },
              { label: 'Responsive Padding', value: '16–48px' },
              { label: 'Grid Columns', value: '12 columns' },
              { label: 'Component Gap', value: '24px default' },
              { label: 'Card Grid Gap', value: '24–32px' },
            ].map(({ label, value }) => (
              <div key={label} style={{ background: '#18181B', border: '1px solid rgba(240,239,244,0.08)', borderRadius: 12, padding: '16px 20px' }}>
                <p style={{ fontSize: 12, color: '#6B7280', margin: '0 0 6px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.08em' }}>{label}</p>
                <p style={{ fontFamily: 'monospace', fontSize: 15, color: '#F0EFF4', margin: 0 }}>{value}</p>
              </div>
            ))}
          </div>
        </div>

        {/* ═══════════════════════════════════════════
            06 — Components
        ═══════════════════════════════════════════ */}
        <div id="section-components" style={s.section}>
          <SectionHeader num="06" title="Components (Web)" />

          {/* Buttons */}
          <p style={{ fontFamily: 'Inter, sans-serif', fontWeight: 600, fontSize: 13, color: '#6B7280', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 16 }}>Buttons</p>
          <div style={{ background: '#18181B', border: '1px solid rgba(240,239,244,0.08)', borderRadius: 16, padding: 28, marginBottom: 32 }}>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, marginBottom: 20 }}>
              <button style={{ background: '#7B61FF', border: 'none', borderRadius: 10, padding: '12px 24px', color: 'white', fontFamily: 'Inter, sans-serif', fontWeight: 600, fontSize: 15, cursor: 'pointer' }}>Primary</button>
              <button style={{ background: 'transparent', border: '1px solid rgba(123,97,255,0.4)', borderRadius: 10, padding: '12px 24px', color: '#F0EFF4', fontFamily: 'Inter, sans-serif', fontWeight: 600, fontSize: 15, cursor: 'pointer' }}>Secondary</button>
              <button style={{ background: 'transparent', border: 'none', borderRadius: 10, padding: '12px 24px', color: '#7B61FF', fontFamily: 'Inter, sans-serif', fontWeight: 600, fontSize: 15, cursor: 'pointer', textDecoration: 'underline' }}>Ghost</button>
              <button style={{ background: '#DC2626', border: 'none', borderRadius: 10, padding: '12px 24px', color: 'white', fontFamily: 'Inter, sans-serif', fontWeight: 600, fontSize: 15, cursor: 'pointer' }}>Destructive</button>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {[
                ['Primary', 'bg #7B61FF · text white · px-6 py-3 · radius 10px · hover bg #6B51EF'],
                ['Secondary', 'transparent bg · border rgba(123,97,255,0.4) · text #F0EFF4'],
                ['Ghost', 'no border · text #7B61FF · hover underline'],
                ['Destructive', 'bg #DC2626 · text white'],
              ].map(([name, spec]) => (
                <p key={name} style={{ fontSize: 13, color: '#9CA3AF', margin: 0 }}>
                  <strong style={{ color: '#D1D5DB' }}>{name}:</strong> <InlineCode>{spec}</InlineCode>
                </p>
              ))}
            </div>
          </div>

          {/* Badges */}
          <p style={{ fontFamily: 'Inter, sans-serif', fontWeight: 600, fontSize: 13, color: '#6B7280', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 16 }}>Badges / Pills</p>
          <div style={{ background: '#18181B', border: '1px solid rgba(240,239,244,0.08)', borderRadius: 16, padding: 28, marginBottom: 32 }}>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, marginBottom: 20 }}>
              {[
                { label: 'Default', bg: '#18181B', border: 'rgba(123,97,255,0.3)', text: '#A5B4FC' },
                { label: 'Success', bg: '#052e16', border: '#16A34A', text: '#4ADE80' },
                { label: 'Warning', bg: '#451a03', border: '#F59E0B', text: '#FCD34D' },
                { label: 'Error', bg: '#450a0a', border: '#DC2626', text: '#FCA5A5' },
              ].map(({ label, bg, border, text }) => (
                <span key={label} style={{
                  background: bg, border: `1px solid ${border}`,
                  borderRadius: 9999, padding: '4px 14px',
                  fontSize: 13, fontWeight: 600, color: text,
                  fontFamily: 'Inter, sans-serif',
                }}>{label}</span>
              ))}
            </div>
          </div>

          {/* Cards */}
          <p style={{ fontFamily: 'Inter, sans-serif', fontWeight: 600, fontSize: 13, color: '#6B7280', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 16 }}>Cards</p>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 20, marginBottom: 32 }}>
            <div style={{ background: '#18181B', border: '1px solid rgba(240,239,244,0.08)', borderRadius: 16, padding: 24, boxShadow: '0 4px 12px rgba(10,10,20,0.5)' }}>
              <p style={{ fontWeight: 700, fontSize: 15, color: '#F0EFF4', margin: '0 0 8px' }}>Standard Card</p>
              <p style={{ fontSize: 14, color: '#9CA3AF', margin: 0, lineHeight: 1.6 }}>bg #18181B · border rgba(240,239,244,0.08) · radius 16px · shadow-md</p>
            </div>
            <div style={{ background: '#18181B', border: '1px solid rgba(123,97,255,0.2)', borderRadius: 16, padding: 24, boxShadow: '0 0 20px rgba(123,97,255,0.2), 0 0 40px rgba(123,97,255,0.1)' }}>
              <p style={{ fontWeight: 700, fontSize: 15, color: '#F0EFF4', margin: '0 0 8px' }}>Featured Card</p>
              <p style={{ fontSize: 14, color: '#9CA3AF', margin: 0, lineHeight: 1.6 }}>+ purple-glow shadow · border rgba(123,97,255,0.2)</p>
            </div>
          </div>

          {/* Block Labels */}
          <p style={{ fontFamily: 'Inter, sans-serif', fontWeight: 600, fontSize: 13, color: '#6B7280', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 16 }}>Block Labels (Conversation Diagrams & Slides)</p>
          <div style={{ background: '#18181B', border: '1px solid rgba(240,239,244,0.08)', borderRadius: 16, padding: 28 }}>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 20 }}>
              {[
                { label: 'SYSTEM', bg: '#F59E0B', text: '#111827' },
                { label: 'YOU SAY', bg: '#E5E7EB', text: '#111827' },
                { label: 'THINKS', bg: '#EA580C', text: 'white' },
                { label: 'RESPONSE', bg: '#DC2626', text: 'white' },
                { label: 'SKILL', bg: '#16A34A', text: 'white' },
              ].map(({ label, bg, text }) => (
                <span key={label} style={{
                  background: bg, borderRadius: 6, padding: '6px 14px',
                  fontSize: 12, fontWeight: 700, color: text,
                  fontFamily: 'monospace', letterSpacing: '0.08em',
                }}>{label}</span>
              ))}
            </div>
            <p style={{ fontSize: 13, color: '#9CA3AF', margin: 0, lineHeight: 1.6 }}>
              Consistent across web diagrams, slide decks, and documentation. These semantic blocks map the conversation loop: System → You → Thinks → Response → Skill.
            </p>
          </div>
        </div>

        {/* ═══════════════════════════════════════════
            07 — Tables
        ═══════════════════════════════════════════ */}
        <div id="section-tables" style={s.section}>
          <SectionHeader num="07" title="Tables" />
          <p style={{ color: '#9CA3AF', fontSize: 16, lineHeight: 1.7, marginBottom: 32, maxWidth: 700 }}>
            Tables appear across three surfaces. Each has specific rules. The unifying principle: no visible vertical gridlines, ever.
          </p>

          {/* Web tables */}
          <p style={{ fontFamily: 'Inter, sans-serif', fontWeight: 600, fontSize: 13, color: '#6B7280', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 16 }}>Web Tables</p>
          <div style={{ background: '#18181B', border: '1px solid rgba(240,239,244,0.08)', borderRadius: 16, overflow: 'hidden', marginBottom: 32 }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ borderBottom: '1px solid rgba(240,239,244,0.08)' }}>
                  {['Component', 'Build', 'Status', 'ETA'].map(h => (
                    <th key={h} style={{ padding: '12px 20px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: '0.1em', background: '#18181B' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {[
                  ['ProLine Sync', 'v2.1', '✅ Live', '—'],
                  ['Email Agent', 'v1.4', '🟡 Testing', '3 days'],
                  ['Lead Qualifier', 'v3.0', '🔵 Planned', '2 weeks'],
                ].map((row, i) => (
                  <tr key={i} style={{ borderBottom: '1px solid rgba(240,239,244,0.05)', background: i % 2 === 1 ? '#111827' : 'transparent' }}>
                    {row.map((cell, j) => (
                      <td key={j} style={{ padding: '12px 20px', fontSize: 14, color: '#D1D5DB' }}>{cell}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
            <div style={{ padding: '12px 20px', background: '#111827', borderTop: '1px solid rgba(240,239,244,0.06)' }}>
              <p style={{ margin: 0, fontSize: 12, color: '#6B7280' }}>Th: bg #18181B · uppercase · tracking-wider · 12px. No vertical cell borders. Hover rows: rgba(123,97,255,0.05).</p>
            </div>
          </div>

          {/* Document tables */}
          <p style={{ fontFamily: 'Inter, sans-serif', fontWeight: 600, fontSize: 13, color: '#6B7280', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 16 }}>Document Tables (Proposals, Reports, PDFs)</p>
          <div style={{ background: '#18181B', border: '1px solid rgba(240,239,244,0.08)', borderRadius: 16, overflow: 'hidden', marginBottom: 32 }}>
            <div style={{ padding: '16px 20px', background: '#0D0D0F', borderBottom: '1px solid #1F2937' }}>
              <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr', gap: 16 }}>
                {['Service', 'Price', 'Qty', 'Total'].map(h => (
                  <span key={h} style={{ fontSize: 13, fontWeight: 700, color: '#F0EFF4', fontFamily: 'Inter, sans-serif' }}>{h}</span>
                ))}
              </div>
            </div>
            {[
              ['RoofClaw Hardware (Mac Mini)', '$3,200', '1', '$3,200'],
              ['Build & Configuration', '$1,200', '1', '$1,200'],
              ['3-Hour Training Session', '$600', '1', '$600'],
            ].map((row, i) => (
              <div key={i} style={{ padding: '14px 20px', background: i % 2 === 0 ? 'white' : '#F9F9F9', borderBottom: '1px solid #E5E7EB' }}>
                <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr', gap: 16 }}>
                  {row.map((cell, j) => (
                    <span key={j} style={{ fontSize: 14, color: '#111827', fontFamily: 'Inter, sans-serif' }}>{cell}</span>
                  ))}
                </div>
              </div>
            ))}
            <div style={{ padding: '12px 20px', background: '#18181B', borderTop: '1px solid rgba(240,239,244,0.06)' }}>
              <p style={{ margin: 0, fontSize: 12, color: '#6B7280' }}>Header: bg #0D0D0F white text · Rows: white/F9F9F9 alternating · Horizontal rules only · No vertical gridlines</p>
            </div>
          </div>

          {/* Slide tables */}
          <p style={{ fontFamily: 'Inter, sans-serif', fontWeight: 600, fontSize: 13, color: '#6B7280', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 16 }}>Slide Deck Tables</p>
          <div style={{ background: '#18181B', border: '1px solid rgba(240,239,244,0.08)', borderRadius: 16, overflow: 'hidden', marginBottom: 32 }}>
            <div style={{ background: '#7B61FF', padding: '12px 20px' }}>
              <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: 20 }}>
                {['Metric', 'Before', 'After'].map(h => (
                  <span key={h} style={{ fontWeight: 700, color: 'white', fontSize: 16, fontFamily: 'Inter, sans-serif' }}>{h}</span>
                ))}
              </div>
            </div>
            {[
              ['Response time', '4 hours', '< 5 min'],
              ['Leads followed up', '30%', '100%'],
              ['Admin hours/week', '20 hrs', '2 hrs'],
            ].map((row, i) => (
              <div key={i} style={{ padding: '14px 20px', background: i % 2 === 0 ? '#18181B' : '#111827' }}>
                <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: 20 }}>
                  {row.map((cell, j) => (
                    <span key={j} style={{ fontSize: 15, color: '#F0EFF4', fontFamily: 'Inter, sans-serif' }}>{cell}</span>
                  ))}
                </div>
              </div>
            ))}
            <div style={{ padding: '12px 20px', background: '#0D0D0F', borderTop: '1px solid rgba(240,239,244,0.06)' }}>
              <p style={{ margin: 0, fontSize: 12, color: '#6B7280' }}>Header: bg #7B61FF white text · Rows: #18181B/#111827 alternating · Min 14pt · Max 6 rows per slide</p>
            </div>
          </div>
        </div>

        {/* ═══════════════════════════════════════════
            08 — Documents
        ═══════════════════════════════════════════ */}
        <div id="section-documents" style={s.section}>
          <SectionHeader num="08" title="Documents (Proposals, Reports, Runbooks)" />
          <p style={{ color: '#9CA3AF', fontSize: 16, lineHeight: 1.7, marginBottom: 32, maxWidth: 700 }}>
            Non-web documents follow a separate standard. Print-friendly for proposals, screen-optimized for PDF deliverables.
          </p>

          {/* Document header preview */}
          <p style={{ fontFamily: 'Inter, sans-serif', fontWeight: 600, fontSize: 13, color: '#6B7280', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 16 }}>Standard Document Header</p>
          <div style={{ background: 'white', borderRadius: 16, overflow: 'hidden', marginBottom: 32, boxShadow: '0 4px 24px rgba(10,10,20,0.4)' }}>
            <div style={{ padding: '20px 32px', borderBottom: '2px solid #7B61FF', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
              <span style={{ fontFamily: 'Inter, sans-serif', fontWeight: 800, fontSize: 20, color: '#0D0D0F' }}>RoofClaw</span>
              <span style={{ fontFamily: 'Inter, sans-serif', fontWeight: 700, fontSize: 16, color: '#111827', textAlign: 'center' }}>Client Onboarding Proposal</span>
              <div style={{ textAlign: 'right' }}>
                <p style={{ fontFamily: 'Inter, sans-serif', fontSize: 13, color: '#6B7280', margin: 0 }}>Client Name</p>
                <p style={{ fontFamily: 'Inter, sans-serif', fontSize: 13, color: '#6B7280', margin: 0 }}>April 22, 2026</p>
              </div>
            </div>
            <div style={{ padding: '24px 32px', background: 'white' }}>
              <div style={{ marginBottom: 20 }}>
                <p style={{ fontFamily: 'Inter, sans-serif', fontWeight: 700, fontSize: 24, color: '#0D0D0F', margin: '0 0 12px', textTransform: 'uppercase', borderBottom: '2px solid #7B61FF', paddingBottom: 6, display: 'inline-block' }}>Scope of Work</p>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                {/* Info callout */}
                <div style={{ borderLeft: '4px solid #3B82F6', background: 'rgba(59,130,246,0.05)', padding: '12px 16px', borderRadius: '0 8px 8px 0' }}>
                  <p style={{ fontFamily: 'Inter, sans-serif', fontSize: 13, color: '#1e40af', fontWeight: 600, margin: '0 0 4px' }}>ℹ️ Note</p>
                  <p style={{ fontFamily: 'Inter, sans-serif', fontSize: 13, color: '#374151', margin: 0, lineHeight: 1.6 }}>All API integrations require valid credentials provided 48 hours before build start.</p>
                </div>
                {/* Warning callout */}
                <div style={{ borderLeft: '4px solid #F59E0B', background: 'rgba(245,158,11,0.05)', padding: '12px 16px', borderRadius: '0 8px 8px 0' }}>
                  <p style={{ fontFamily: 'Inter, sans-serif', fontSize: 13, color: '#92400e', fontWeight: 600, margin: '0 0 4px' }}>⚠️ Warning</p>
                  <p style={{ fontFamily: 'Inter, sans-serif', fontSize: 13, color: '#374151', margin: 0, lineHeight: 1.6 }}>Missing credentials will delay the build schedule and may push the delivery date.</p>
                </div>
              </div>
            </div>
            <div style={{ padding: '12px 32px', background: '#F9F9F9', borderTop: '1px solid #E5E7EB', display: 'flex', justifyContent: 'space-between' }}>
              <span style={{ fontFamily: 'Inter, sans-serif', fontSize: 11, color: '#9CA3AF' }}>RoofClaw · roofclaw.com</span>
              <span style={{ fontFamily: 'Inter, sans-serif', fontSize: 11, color: '#9CA3AF' }}>Page 1</span>
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 20, marginBottom: 32 }}>
            {[
              { label: 'H1 Document', spec: 'Inter Bold 24pt · CAPS · purple underline bar', bg: '#F59E0B', h: true },
              { label: 'H2 Section', spec: 'Inter SemiBold 16pt · #111827', bg: '#7B61FF' },
              { label: 'H3 Subsection', spec: 'Inter SemiBold 13pt · #111827', bg: '#6B51EF' },
              { label: 'Body Text', spec: 'Inter Regular 11pt · line-height 1.6 · #111827 (not #000)', bg: '#9CA3AF' },
              { label: 'Callout: Info', spec: '4px solid #3B82F6 · bg rgba(59,130,246,0.05)', bg: '#3B82F6' },
              { label: 'Callout: Critical', spec: '4px solid #DC2626 · bg rgba(220,38,38,0.05)', bg: '#DC2626' },
            ].map(({ label, spec, bg }) => (
              <div key={label} style={{ background: '#18181B', border: '1px solid rgba(240,239,244,0.08)', borderRadius: 12, padding: '16px 20px', display: 'flex', gap: 14, alignItems: 'flex-start' }}>
                <div style={{ width: 4, height: 40, background: bg, borderRadius: 2, flexShrink: 0, marginTop: 2 }} />
                <div>
                  <p style={{ fontWeight: 600, fontSize: 14, color: '#F0EFF4', margin: '0 0 4px' }}>{label}</p>
                  <p style={{ fontSize: 13, color: '#9CA3AF', margin: 0, lineHeight: 1.5 }}>{spec}</p>
                </div>
              </div>
            ))}
          </div>

          <RuleCallout label="Document Rules" items={[
            "Primary text uses #111827 for print (not pure black — ink bleeds better)",
            "Margins: 1 inch all sides (25mm)",
            "Footer: page number right-aligned + 'RoofClaw · roofclaw.com' left-aligned",
            "Purple horizontal rule (2px #7B61FF) separates document header from body",
            "Callout boxes: 4px border-left + 5% opacity bg tint — Info/Warning/Tip/Critical",
          ]} />
        </div>

        {/* ═══════════════════════════════════════════
            09 — Slide Decks
        ═══════════════════════════════════════════ */}
        <div id="section-slides" style={s.section}>
          <SectionHeader num="09" title="Slide Decks" />
          <p style={{ color: '#9CA3AF', fontSize: 16, lineHeight: 1.7, marginBottom: 32, maxWidth: 700 }}>
            Every RoofClaw presentation runs dark. The color block system from web carries directly into slides — consistent language across all surfaces.
          </p>

          {/* Slide mockup */}
          <div style={{ marginBottom: 32 }}>
            <p style={{ fontFamily: 'Inter, sans-serif', fontWeight: 600, fontSize: 13, color: '#6B7280', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 16 }}>Default Slide — Dark</p>
            <div style={{
              background: '#0D0D0F', borderRadius: 16, aspectRatio: '16/9', maxWidth: 700,
              padding: '40px 64px', display: 'flex', flexDirection: 'column', justifyContent: 'space-between',
              border: '1px solid rgba(240,239,244,0.06)', position: 'relative', overflow: 'hidden',
            }}>
              <div style={{ position: 'absolute', inset: 0, background: 'radial-gradient(ellipse at center, rgba(26,16,64,0.6) 0%, transparent 60%)', pointerEvents: 'none' }} />
              <div style={{ position: 'relative', zIndex: 1 }}>
                <p style={{ fontFamily: 'Inter, sans-serif', fontWeight: 700, fontSize: 12, letterSpacing: '0.15em', color: '#7B61FF', textTransform: 'uppercase', marginBottom: 16 }}>ROOFCLAW · 2026</p>
                <h2 style={{ fontFamily: 'Inter, sans-serif', fontWeight: 900, fontSize: 'clamp(24px,4vw,48px)', color: '#F0EFF4', lineHeight: 1.1, margin: '0 0 16px' }}>
                  Your Agent Works<br />While You Sleep.
                </h2>
                <p style={{ fontFamily: 'Inter, sans-serif', fontSize: 'clamp(12px,1.5vw,16px)', color: '#9CA3AF', lineHeight: 1.6, maxWidth: 400, margin: 0 }}>
                  Connected to ProLine, your email, and your calendar — 24/7.
                </p>
              </div>
              <div style={{ display: 'flex', gap: 8, position: 'relative', zIndex: 1 }}>
                {['SYSTEM', 'THINKS', 'RESPONSE', 'SKILL'].map((label, i) => {
                  const colors = [
                    { bg: '#F59E0B', text: '#111' },
                    { bg: '#EA580C', text: 'white' },
                    { bg: '#DC2626', text: 'white' },
                    { bg: '#16A34A', text: 'white' },
                  ];
                  return (
                    <span key={label} style={{
                      background: colors[i].bg, borderRadius: 5, padding: '5px 10px',
                      fontSize: 11, fontWeight: 700, color: colors[i].text,
                      fontFamily: 'monospace', letterSpacing: '0.06em',
                    }}>{label}</span>
                  );
                })}
              </div>
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 20, marginBottom: 32 }}>
            {[
              { label: 'Slide Layout Grid', items: ['Safe zone: 80px padding all sides', 'Heading zone: top 20%', 'Content zone: middle 60%', 'Caption zone: bottom 20%'] },
              { label: 'Progressive Reveal', items: ['Each concept = one click', 'Max 3–4 concepts per slide', 'Captions appear last', 'Diagrams build element by element'] },
              { label: 'Typography Scale', items: ['Display: Inter 900 · 52-64px', 'Section: Inter 700 · 36px', 'Body: Inter 600 · 22px', 'Caption: Inter 400 · 15-16px · #9CA3AF'] },
            ].map(({ label, items }) => (
              <div key={label} style={{ background: '#18181B', border: '1px solid rgba(240,239,244,0.08)', borderRadius: 16, padding: 24 }}>
                <p style={{ fontWeight: 700, fontSize: 14, color: '#F0EFF4', margin: '0 0 14px' }}>{label}</p>
                <ul style={{ margin: 0, padding: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {items.map(item => (
                    <li key={item} style={{ display: 'flex', alignItems: 'flex-start', gap: 8, fontSize: 13, color: '#9CA3AF', lineHeight: 1.5 }}>
                      <span style={{ color: '#7B61FF', flexShrink: 0 }}>→</span>
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>

          <RuleCallout label="Slide Rules" items={[
            "Background always dark — never white slides unless a specific contrast moment demands it",
            "The SYSTEM/THINKS/RESPONSE/SKILL/YOU SAY block system is consistent across all presentations",
            "Never show more than 3–4 concepts on a single slide",
            "Skill boxes: green bg, rounded corners, font-mono label",
            "Captions always appear LAST — after the visual has landed",
          ]} />
        </div>

        {/* ═══════════════════════════════════════════
            10 — Glass & Effects
        ═══════════════════════════════════════════ */}
        <div id="section-glass" style={s.section}>
          <SectionHeader num="10" title="Glass & Effects" />

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 24, marginBottom: 32 }}>
            {/* Glass dark demo */}
            <div>
              <p style={{ fontFamily: 'Inter, sans-serif', fontWeight: 600, fontSize: 13, color: '#6B7280', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 16 }}>Glass Dark</p>
              <div style={{ position: 'relative', borderRadius: 16, overflow: 'hidden', height: 180 }}>
                <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(135deg, #7B61FF 0%, #EA580C 50%, #16A34A 100%)' }} />
                <div style={{
                  position: 'absolute', inset: 0,
                  backdropFilter: 'blur(20px) saturate(180%)',
                  background: 'rgba(24, 24, 27, 0.8)',
                  border: '1px solid rgba(240, 239, 244, 0.08)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  <p style={{ color: '#F0EFF4', fontWeight: 600, fontSize: 15, fontFamily: 'Inter, sans-serif' }}>Glass Dark Effect</p>
                </div>
              </div>
              <CodeBlock>{`backdrop-filter: blur(20px) saturate(180%);
background: rgba(24, 24, 27, 0.8);
border: 1px solid rgba(240, 239, 244, 0.08);`}</CodeBlock>
            </div>

            {/* Purple glow demo */}
            <div>
              <p style={{ fontFamily: 'Inter, sans-serif', fontWeight: 600, fontSize: 13, color: '#6B7280', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 16 }}>Purple Glow</p>
              <div style={{
                height: 180, borderRadius: 16, background: '#18181B',
                border: '1px solid rgba(123,97,255,0.3)',
                boxShadow: '0 0 20px rgba(123,97,255,0.2), 0 0 40px rgba(123,97,255,0.1)',
                display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 12,
              }}>
                <p style={{ color: '#A5B4FC', fontWeight: 600, fontSize: 15, fontFamily: 'Inter, sans-serif' }}>Featured Element</p>
              </div>
              <CodeBlock>{`box-shadow:
  0 0 20px rgba(123,97,255,0.2),
  0 0 40px rgba(123,97,255,0.1);`}</CodeBlock>
            </div>
          </div>

          <p style={{ fontFamily: 'Inter, sans-serif', fontWeight: 600, fontSize: 13, color: '#6B7280', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 16 }}>Gradient Overlay (Hero Sections)</p>
          <div style={{ position: 'relative', borderRadius: 16, overflow: 'hidden', height: 200, marginBottom: 16 }}>
            <div style={{ position: 'absolute', inset: 0, background: '#0D0D0F' }} />
            <div style={{ position: 'absolute', inset: 0, background: 'radial-gradient(circle at 50% 0%, rgba(123,97,255,0.25) 0%, transparent 55%)' }} />
            <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 8 }}>
              <p style={{ fontFamily: 'Inter, sans-serif', fontWeight: 900, fontSize: 28, color: '#F0EFF4', margin: 0 }}>Hero Section Glow</p>
              <p style={{ fontSize: 14, color: '#7B61FF', margin: 0 }}>radial-gradient from top</p>
            </div>
          </div>
          <CodeBlock>{`background: radial-gradient(
  circle at 50% 0%,
  rgba(123,97,255,0.12) 0%,
  transparent 55%
);`}</CodeBlock>
        </div>

        {/* ═══════════════════════════════════════════
            11 — Animation Tokens
        ═══════════════════════════════════════════ */}
        <div id="section-animation" style={s.section}>
          <SectionHeader num="11" title="Animation Tokens" />

          <div style={{ background: '#18181B', border: '1px solid rgba(240,239,244,0.08)', borderRadius: 16, overflow: 'hidden', marginBottom: 32 }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ borderBottom: '1px solid rgba(240,239,244,0.08)' }}>
                  {['Token', 'Duration', 'Use'].map(h => (
                    <th key={h} style={{ padding: '12px 20px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: '0.1em', background: '#18181B' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {[
                  ['instant', '100ms', 'Micro-interactions, icon swaps'],
                  ['fast', '200ms', 'Hover states, reveals, dropdowns'],
                  ['base', '300ms', 'Transitions, modal open/close'],
                  ['slow', '500ms', 'Page transitions'],
                  ['slower', '800ms', 'Complex multi-step sequences'],
                ].map(([token, duration, use], i) => (
                  <tr key={token} style={{ borderBottom: '1px solid rgba(240,239,244,0.05)', background: i % 2 === 1 ? '#111827' : 'transparent' }}>
                    <td style={{ padding: '12px 20px', fontFamily: 'monospace', fontSize: 13, color: '#7B61FF' }}>{token}</td>
                    <td style={{ padding: '12px 20px', fontFamily: 'monospace', fontSize: 13, color: '#D1D5DB' }}>{duration}</td>
                    <td style={{ padding: '12px 20px', fontSize: 13, color: '#9CA3AF' }}>{use}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 20, marginBottom: 32 }}>
            <div>
              <p style={{ fontFamily: 'Inter, sans-serif', fontWeight: 600, fontSize: 13, color: '#6B7280', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 12 }}>Default Easing</p>
              <CodeBlock>{`cubic-bezier(0.16, 1, 0.3, 1)`}</CodeBlock>
              <p style={{ fontSize: 13, color: '#9CA3AF', marginTop: 8 }}>Ease-out. Snappy start, smooth end. Use for most transitions.</p>
            </div>
            <div>
              <p style={{ fontFamily: 'Inter, sans-serif', fontWeight: 600, fontSize: 13, color: '#6B7280', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 12 }}>Spring Easing</p>
              <CodeBlock>{`cubic-bezier(0.34, 1.56, 0.64, 1)`}</CodeBlock>
              <p style={{ fontSize: 13, color: '#9CA3AF', marginTop: 8 }}>Slight overshoot. Use for modals, cards that pop in, attention-drawing moments.</p>
            </div>
          </div>

          <div style={{ background: 'rgba(245,158,11,0.1)', border: '1px solid rgba(245,158,11,0.3)', borderRadius: 12, padding: '16px 20px' }}>
            <p style={{ fontWeight: 700, fontSize: 13, color: '#F59E0B', margin: '0 0 6px' }}>⚠️ ACCESSIBILITY — prefers-reduced-motion</p>
            <p style={{ fontSize: 14, color: '#D1D5DB', margin: 0, lineHeight: 1.6 }}>
              Always include a <InlineCode>@media (prefers-reduced-motion: reduce)</InlineCode> block that sets all animation and transition durations to 0ms. Non-negotiable.
            </p>
          </div>
        </div>

        {/* ═══════════════════════════════════════════
            12 — AI Design Prompt
        ═══════════════════════════════════════════ */}
        <div id="section-prompt" style={{ ...s.section, borderBottom: 'none' }}>
          <SectionHeader num="12" title="The AI Design Prompt" />
          <p style={{ color: '#9CA3AF', fontSize: 16, lineHeight: 1.7, marginBottom: 32, maxWidth: 700 }}>
            A terse, AI-optimized version of this entire document. Paste it into Claude, ChatGPT, Cursor, or any AI design tool before asking it to create RoofClaw assets.
          </p>

          <div style={{ background: '#18181B', border: '1px solid rgba(123,97,255,0.2)', borderRadius: 20, overflow: 'hidden', marginBottom: 32, boxShadow: '0 0 40px rgba(123,97,255,0.1)' }}>
            <div style={{ padding: '20px 28px', borderBottom: '1px solid rgba(240,239,244,0.08)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
              <div>
                <p style={{ fontFamily: 'Inter, sans-serif', fontWeight: 700, fontSize: 11, letterSpacing: '0.18em', color: '#7B61FF', textTransform: 'uppercase', marginBottom: 4 }}>roofclaw-design-standards.md</p>
                <p style={{ fontSize: 14, color: '#9CA3AF', margin: 0 }}>AI-optimized · covers web, docs, tables, slides · ~200 rules</p>
              </div>
              <div style={{ display: 'flex', gap: 10 }}>
                <button
                  onClick={() => setModalOpen(true)}
                  style={{
                    background: 'linear-gradient(135deg, #7B61FF 0%, #6B51EF 100%)',
                    border: 'none', borderRadius: 10, padding: '10px 20px',
                    color: 'white', fontWeight: 600, fontSize: 14, cursor: 'pointer',
                    display: 'flex', alignItems: 'center', gap: 8, fontFamily: 'Inter, sans-serif',
                  }}
                >
                  <Copy size={15} />
                  Copy AI Prompt
                </button>
                <a
                  href="/downloads/roofclaw-design-standards.md"
                  download="roofclaw-design-standards.md"
                  style={{
                    background: 'rgba(240,239,244,0.06)',
                    border: '1px solid rgba(240,239,244,0.12)',
                    borderRadius: 10, padding: '10px 20px',
                    color: '#F0EFF4', fontWeight: 600, fontSize: 14,
                    display: 'flex', alignItems: 'center', gap: 8,
                    textDecoration: 'none', fontFamily: 'Inter, sans-serif',
                  }}
                >
                  <Download size={15} />
                  Download .md
                </a>
              </div>
            </div>
            <div style={{ padding: '20px 28px' }}>
              <pre style={{
                margin: 0, fontFamily: 'monospace', fontSize: 13,
                color: '#D1D5DB', lineHeight: 1.7,
                whiteSpace: 'pre-wrap', wordBreak: 'break-word',
                maxHeight: 400, overflow: 'hidden',
                position: 'relative',
              }}>
                {DESIGN_STANDARDS_MD.split('\n').slice(0, 30).join('\n')}
              </pre>
              <div style={{
                height: 80, background: 'linear-gradient(to bottom, transparent, #18181B)',
                marginTop: -80, position: 'relative',
                display: 'flex', alignItems: 'flex-end', justifyContent: 'center', paddingBottom: 16,
              }}>
                <button
                  onClick={() => setModalOpen(true)}
                  style={{
                    background: 'rgba(123,97,255,0.15)', border: '1px solid rgba(123,97,255,0.3)',
                    borderRadius: 8, padding: '8px 20px', color: '#A5B4FC',
                    fontSize: 14, cursor: 'pointer', fontFamily: 'Inter, sans-serif', fontWeight: 600,
                    display: 'flex', alignItems: 'center', gap: 6,
                  }}
                >
                  View full prompt <ChevronRight size={14} />
                </button>
              </div>
            </div>
          </div>

          {/* Bottom CTA row */}
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', justifyContent: 'center', paddingBottom: 64 }}>
            <button
              onClick={() => setModalOpen(true)}
              style={{
                background: 'linear-gradient(135deg, #7B61FF 0%, #6B51EF 100%)',
                border: 'none', borderRadius: 10, padding: '14px 28px',
                color: 'white', fontWeight: 600, fontSize: 16, cursor: 'pointer',
                display: 'flex', alignItems: 'center', gap: 10, fontFamily: 'Inter, sans-serif',
                boxShadow: '0 0 24px rgba(123,97,255,0.35)',
              }}
            >
              <Copy size={18} />
              Copy AI Design Prompt
            </button>
            <a
              href="/downloads/roofclaw-design-standards.md"
              download="roofclaw-design-standards.md"
              style={{
                background: 'rgba(240,239,244,0.06)',
                border: '1px solid rgba(240,239,244,0.12)',
                borderRadius: 10, padding: '14px 28px',
                color: '#F0EFF4', fontWeight: 600, fontSize: 16,
                display: 'flex', alignItems: 'center', gap: 10,
                textDecoration: 'none', fontFamily: 'Inter, sans-serif',
              }}
            >
              <Download size={18} />
              Download .md
            </a>
          </div>
        </div>

      </div>
    </div>
  );
}
